import { memo, type MouseEvent } from 'react';
import classNames from 'classnames';

import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconHeartFilled } from '@picsart/design-system/foundation/icons/IconHeartFilled';
import { IconHeartOutline } from '@picsart/design-system/foundation/icons/IconHeartOutline';
import { useTranslator } from '@picsart/rc/services/localization';

import { usePresetLike } from '@/features/preset-likes';
import useStyles from './styles';

import type { PresetLikeButtonProps } from './types';

const PresetLikeButton = ({
  documentId,
  slug,
  screenType,
  size = 'default',
  className,
}: PresetLikeButtonProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { isLiked, isVisible, toggle } = usePresetLike({
    documentId,
    slug,
    screenType,
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    toggle();
  };

  if (!isVisible) return null;

  const LikeIcon = isLiked ? IconHeartFilled : IconHeartOutline;
  const label = translate('Like');

  // On a card the heart is the same DS button as the one it sits beside — the
  // copy-link chip on desktop, the save bookmark on mobile — given the same
  // `size` and the host's own class, so the pair matches by construction rather
  // than by two stylesheets agreeing. `cardLiked` only recolours it; this sheet
  // is injected after the host's, so it wins the tie.
  if (size === 'card') {
    return (
      <InlineButton
        size="small"
        centerIcon={LikeIcon}
        className={classNames(className, {
          [classes.cardLiked]: isLiked,
        })}
        isWithoutBorder
        onClick={handleClick}
        aria-pressed={isLiked}
        aria-label={label}
        dataTestId="preset-like"
      />
    );
  }

  return (
    <button
      type="button"
      className={classNames(classes.likeButton, className)}
      onClick={handleClick}
      onKeyDown={(event) => event.stopPropagation()}
      aria-pressed={isLiked}
      aria-label={label}
      data-testid="preset-like"
    >
      <LikeIcon
        className={classNames(classes.likeIcon, {
          [classes.likeIconLiked]: isLiked,
        })}
        fill="currentColor"
      />
      <span className={classes.likeLabel}>{label}</span>
    </button>
  );
};

export default memo(PresetLikeButton);
